/**
 * Drone permit upload, status helpers, and admin notifications.
 */

export const DRONE_PERMIT_FILE_PREFIX = 'drone_permit';

export const DRONE_PERMIT_ALLOWED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/pdf',
];

export const DRONE_PERMIT_MAX_SIZE = 10 * 1024 * 1024;

export const isDronePermitFile = (fileName) =>
  typeof fileName === 'string' && fileName.startsWith(DRONE_PERMIT_FILE_PREFIX);

export const isIdentityVerificationFile = (fileName) =>
  typeof fileName === 'string'
  && !isDronePermitFile(fileName)
  && !fileName.startsWith('freelance_permit'); // see freelancePermitService.js

export const photographerUsesDrone = (shootingMethods) =>
  Array.isArray(shootingMethods) && shootingMethods.includes('drone');

export const getDronePermitStatus = (profile) => {
  if (!profile?.drone_permit_url) return 'not_uploaded';
  if (!profile.drone_permit_reviewed_at) return 'submitted';
  if (profile.drone_permit_verified === true) return 'approved';
  return 'rejected';
};

export const getDronePermitStatusLabel = (status) => {
  switch (status) {
    case 'approved':
      return 'مقبول';
    case 'submitted':
      return 'قيد المراجعة';
    case 'rejected':
      return 'مرفوض';
    default:
      return 'لم يُرفع بعد';
  }
};

export const getDaysUntilExpiry = (expiresAt) => {
  if (!expiresAt) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = new Date(expiresAt);
  if (Number.isNaN(expiry.getTime())) return null;
  expiry.setHours(0, 0, 0, 0);
  return Math.ceil((expiry.getTime() - today.getTime()) / 86400000);
};

/**
 * - no_date: admin has not set expiry yet
 * - valid: more than 30 days remaining
 * - expiring_soon: 8–30 days remaining
 * - critical: 1–7 days remaining
 * - expired: past expiry date
 */
export const getDroneExpiryStatus = (expiresAt) => {
  const daysLeft = getDaysUntilExpiry(expiresAt);
  if (daysLeft === null) return 'no_date';
  if (daysLeft < 0) return 'expired';
  if (daysLeft <= 7) return 'critical';
  if (daysLeft <= 30) return 'expiring_soon';
  return 'valid';
};

export const getDroneExpiryStatusLabel = (status) => {
  switch (status) {
    case 'valid':
      return 'ساري';
    case 'expiring_soon':
      return 'يقارب على الانتهاء';
    case 'critical':
      return 'ينتهي قريباً';
    case 'expired':
      return 'منتهي';
    default:
      return 'بدون تاريخ انتهاء';
  }
};

export const getDroneExpiryBadgeClass = (status) => {
  switch (status) {
    case 'valid':
      return 'bg-green-100 text-green-700';
    case 'expiring_soon':
      return 'bg-amber-100 text-amber-800';
    case 'critical':
      return 'bg-orange-100 text-orange-800';
    case 'expired':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-slate-100 text-slate-600';
  }
};

export const validateDronePermitFile = (file) => {
  if (!file) return 'لم يتم اختيار ملف';
  if (!DRONE_PERMIT_ALLOWED_TYPES.includes(file.type)) {
    return 'يُقبل JPEG أو PNG أو WebP أو PDF';
  }
  if (file.size > DRONE_PERMIT_MAX_SIZE) {
    return 'الحجم الأقصى 10 ميغابايت';
  }
  return null;
};

export const notifyAdminDronePermitUpload = async (supabase, {
  userId,
  photographerName,
  documentUrl,
  isReplacement = false,
}) => {
  try {
    await supabase.from('admin_notifications').insert({
      type: 'drone_permit_request',
      title: isReplacement ? '🔄 تحديث تصريح درون' : '🛸 طلب مراجعة تصريح درون',
      message: isReplacement
        ? `المصور "${photographerName}" استبدل تصريح الدرون ويحتاج مراجعة جديدة.`
        : `المصور "${photographerName}" رفع تصريح درون ويحتاج مراجعة.`,
      related_user_id: userId,
      related_data: {
        photographer_name: photographerName,
        document_url: documentUrl,
        is_replacement: isReplacement,
        submitted_at: new Date().toISOString(),
      },
    });
  } catch (err) {
    console.warn('Failed to create admin drone permit notification:', err);
  }
};

/**
 * Upload drone permit to storage, update photographer_profiles, notify admin.
 */
export const uploadDronePermit = async (supabase, {
  userId,
  file,
  photographerName = 'مصور',
  previousUrl = null,
}) => {
  const validationError = validateDronePermitFile(file);
  if (validationError) throw new Error(validationError);
  if (!userId) throw new Error('معرّف المستخدم غير متوفر');

  const ext = file.name.split('.').pop();
  const filePath = `${userId}/${DRONE_PERMIT_FILE_PREFIX}_${Date.now()}.${ext}`;

  const { error: uploadError } = await supabase.storage
    .from('verification-docs')
    .upload(filePath, file, { cacheControl: '3600', upsert: false });

  if (uploadError) throw uploadError;

  const { data: urlData } = supabase.storage
    .from('verification-docs')
    .getPublicUrl(filePath);

  const publicUrl = urlData?.publicUrl || null;
  const now = new Date().toISOString();
  const isReplacement = !!previousUrl;

  const { error: updateError } = await supabase
    .from('photographer_profiles')
    .update({
      drone_permit_url: publicUrl,
      drone_permit_verified: false,
      drone_permit_submitted_at: now,
      drone_permit_reviewed_at: null,
      drone_permit_rejection_reason: null,
      drone_permit_expires_at: null,
      updated_at: now,
    })
    .eq('user_id', userId);

  if (updateError) throw updateError;

  await notifyAdminDronePermitUpload(supabase, {
    userId,
    photographerName,
    documentUrl: publicUrl,
    isReplacement,
  });

  return { publicUrl, filePath };
};

/**
 * Backfill drone_permit_url from storage when legacy file exists.
 */
export const backfillDronePermitFromStorage = async (supabase, userId, files) => {
  if (!userId || !files?.length) return null;

  const droneFile = files
    .filter((f) => isDronePermitFile(f.name))
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];

  if (!droneFile) return null;

  const { data: urlData } = supabase.storage
    .from('verification-docs')
    .getPublicUrl(`${userId}/${droneFile.name}`);

  const publicUrl = urlData?.publicUrl || null;
  if (!publicUrl) return null;

  const submittedAt = droneFile.created_at || new Date().toISOString();

  await supabase
    .from('photographer_profiles')
    .update({
      drone_permit_url: publicUrl,
      drone_permit_submitted_at: submittedAt,
      updated_at: new Date().toISOString(),
    })
    .eq('user_id', userId);

  return publicUrl;
};

export const matchesDroneFilter = (profile, filterKey) => {
  if (!photographerUsesDrone(profile?.shooting_methods)) return false;

  const permitStatus = getDronePermitStatus(profile);

  if (filterKey === 'drone_pending') {
    return permitStatus === 'submitted';
  }

  if (permitStatus !== 'approved') return false;

  const expiryStatus = getDroneExpiryStatus(profile?.drone_permit_expires_at);
  switch (filterKey) {
    case 'drone_expiring_soon':
      return expiryStatus === 'expiring_soon';
    case 'drone_critical':
      return expiryStatus === 'critical';
    case 'drone_expired':
      return expiryStatus === 'expired';
    case 'drone_valid':
      return expiryStatus === 'valid';
    case 'drone_no_expiry':
      return expiryStatus === 'no_date';
    default:
      return false;
  }
};

export const sortByDroneExpiry = (list, ascending = true) => {
  return [...list].sort((a, b) => {
    const aDate = a.drone_permit_expires_at ? new Date(a.drone_permit_expires_at).getTime() : Infinity;
    const bDate = b.drone_permit_expires_at ? new Date(b.drone_permit_expires_at).getTime() : Infinity;
    return ascending ? aDate - bDate : bDate - aDate;
  });
};

export const syncShootingMethodsToProfile = async (supabase, userId, shootingMethods, shootingMethodModels) => {
  if (!userId) return;
  const { error } = await supabase
    .from('photographer_profiles')
    .update({
      shooting_methods: Array.isArray(shootingMethods) ? shootingMethods : [],
      shooting_method_models: shootingMethodModels || {},
      updated_at: new Date().toISOString(),
    })
    .eq('user_id', userId);
  if (error) throw error;
};
